import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { privacyNoticeApi, processingApi, organizationApi } from '../services/api';
import {
  PrivacyNoticeCreate,
  PrivacyNoticeChannel,
  ProcessingActivity,
  Language
} from '@dsgvo-buddy/types';

interface PrivacyNoticeWizardPageProps {
  editMode?: boolean;
}

import {
  Title,
  Text,
  Button,
  Input,
  Select,
  Option,
  CheckBox,
  ProgressIndicator,
  FlexBox,
  Card,
  Label,
  Icon,
  MessageStrip,
  BusyIndicator,
  RadioButton
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';

const channelConfig: Record<PrivacyNoticeChannel, { label: string; description: string }> = {
  [PrivacyNoticeChannel.WEBSITE]: {
    label: 'Website',
    description: 'Datenschutzhinweise für Ihre Website'
  },
  [PrivacyNoticeChannel.APP]: {
    label: 'App',
    description: 'Datenschutzhinweise für Ihre mobile App'
  },
  [PrivacyNoticeChannel.APPLICATION_FORM]: {
    label: 'Bewerbungsformular',
    description: 'Hinweise für Bewerber'
  },
  [PrivacyNoticeChannel.EMPLOYMENT]: {
    label: 'Beschäftigung',
    description: 'Hinweise für Mitarbeiter'
  },
  [PrivacyNoticeChannel.NEWSLETTER]: {
    label: 'Newsletter',
    description: 'Hinweise für Newsletter-Empfänger'
  },
  [PrivacyNoticeChannel.CUSTOM]: {
    label: 'Benutzerdefiniert',
    description: 'Individueller Datenschutzhinweis'
  }
};

const languageOptions = [
  { value: Language.DE, label: 'Deutsch' },
  { value: Language.EN, label: 'Englisch' }
];

const steps = [
  { title: 'Grunddaten', icon: 'hint' },
  { title: 'Verarbeitungen', icon: 'documents' },
  { title: 'Vorschau', icon: 'accept' }
];

interface NoticeFormData {
  organization_id: number | null;
  title: string;
  channel: PrivacyNoticeChannel;
  language: Language;
}

export default function PrivacyNoticeWizardPage({ editMode = false }: PrivacyNoticeWizardPageProps) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [currentStep, setCurrentStep] = useState(0);
  const [processingActivities, setProcessingActivities] = useState<ProcessingActivity[]>([]);
  const [selectedPaIds, setSelectedPaIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(editMode);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<NoticeFormData>({
    organization_id: null,
    title: '',
    channel: PrivacyNoticeChannel.WEBSITE,
    language: Language.DE
  });

  // In edit mode, only steps 1 and 2 are shown (no preview step)
  const visibleSteps = editMode ? steps.slice(0, 2) : steps;
  const lastStep = visibleSteps.length - 1;

  useEffect(() => { loadData(); }, [id, editMode]);

  const loadData = async () => {
    const orgsResponse = await organizationApi.getAll();

    if (orgsResponse.success && orgsResponse.data) {
      const org = orgsResponse.data[0];

      if (org) {
        setFormData(prev => ({ ...prev, organization_id: org.id }));

        // Load all processing activities for the org
        const paResponse = await processingApi.getByOrganization(org.id);
        if (paResponse.success && paResponse.data) {
          setProcessingActivities(paResponse.data);
        }

        if (editMode && id) {
          const noticeResponse = await privacyNoticeApi.getById(parseInt(id), org.id);
          if (noticeResponse.success && noticeResponse.data) {
            const notice = noticeResponse.data;
            setFormData({
              organization_id: notice.organization_id,
              title: notice.title,
              channel: notice.channel,
              language: notice.language
            });
            // Pre-check currently linked processing activities
            if (notice.processing_activities) {
              setSelectedPaIds(notice.processing_activities.map(pa => pa.id));
            }
          } else {
            navigate('/privacy-notices');
            return;
          }
        }
      }
    }

    setInitialLoading(false);
  };

  const updateFormData = (field: keyof NoticeFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const togglePaSelection = (paId: number) => {
    setSelectedPaIds(prev =>
      prev.includes(paId) ? prev.filter(id => id !== paId) : [...prev, paId]
    );
  };

  const validateStep = (): boolean => {
    switch (currentStep) {
      case 0: return !!(formData.title && formData.channel && formData.language);
      case 1: return selectedPaIds.length > 0;
      case 2: return true;
      default: return false;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, lastStep));
      setError(null);
    } else {
      if (currentStep === 1) {
        setError('Bitte wählen Sie mindestens eine Verarbeitung aus.');
      } else {
        setError('Bitte füllen Sie alle erforderlichen Felder aus.');
      }
    }
  };

  const handleBack = () => { setCurrentStep(prev => Math.max(prev - 1, 0)); setError(null); };

  const handleSubmit = async () => {
    if (!formData.organization_id) { setError('Organisation erforderlich'); return; }
    if (!validateStep()) {
      setError(currentStep === 1
        ? 'Bitte wählen Sie mindestens eine Verarbeitung aus.'
        : 'Bitte füllen Sie alle erforderlichen Felder aus.'
      );
      return;
    }

    setLoading(true);
    setError(null);

    if (editMode && id) {
      // Update basic notice metadata
      const updateResponse = await privacyNoticeApi.update(parseInt(id), formData.organization_id, {
        title: formData.title,
        channel: formData.channel,
        language: formData.language,
        organization_id: formData.organization_id
      });

      if (!updateResponse.success) {
        setError(updateResponse.error || 'Fehler beim Aktualisieren');
        setLoading(false);
        return;
      }

      // Reconcile PA links: determine adds and removes
      const existingIds = (updateResponse.data?.processing_activities ?? []).map(pa => pa.id);
      const toAdd = selectedPaIds.filter(pid => !existingIds.includes(pid));
      const toRemove = existingIds.filter(pid => !selectedPaIds.includes(pid));

      await Promise.all([
        ...toAdd.map(pid => privacyNoticeApi.linkActivity(parseInt(id), pid, formData.organization_id!)),
        ...toRemove.map(pid => privacyNoticeApi.unlinkActivity(parseInt(id), pid, formData.organization_id!))
      ]);

      setLoading(false);
      navigate(`/privacy-notices/${id}`);
    } else {
      const createData: PrivacyNoticeCreate = {
        organization_id: formData.organization_id,
        title: formData.title,
        channel: formData.channel,
        language: formData.language,
        processing_activity_ids: selectedPaIds
      };

      const response = await privacyNoticeApi.create(createData);
      if (response.success && response.data) {
        setLoading(false);
        navigate(`/privacy-notices/${response.data.id}`);
      } else {
        setError(response.error || 'Fehler beim Erstellen');
        setLoading(false);
      }
    }
  };

  // Step 1: Grunddaten
  const renderStep1 = () => (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Titel</Label>
        <Input
          value={formData.title}
          onInput={(e: any) => updateFormData('title', e.target.value)}
          placeholder="z.B. Datenschutzhinweise Website"
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <Label required style={{ display: 'block', marginBottom: '0.5rem' }}>Kanal</Label>
        <FlexBox direction="Column" style={{ gap: '0.5rem' }}>
          {Object.entries(channelConfig).map(([key, { label, description }]) => {
            const isSelected = formData.channel === key;
            return (
              <div
                key={key}
                onClick={() => updateFormData('channel', key as PrivacyNoticeChannel)}
                style={{
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  border: isSelected
                    ? '2px solid var(--sapBrandColor)'
                    : '1px solid var(--sapField_BorderColor)',
                  backgroundColor: isSelected ? 'rgba(0, 112, 242, 0.06)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                <RadioButton
                  name="channel"
                  text={label}
                  checked={isSelected}
                  onChange={() => updateFormData('channel', key as PrivacyNoticeChannel)}
                />
                <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', marginLeft: '1.75rem', display: 'block' }}>
                  {description}
                </Text>
              </div>
            );
          })}
        </FlexBox>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Sprache</Label>
        <Select
          onChange={(e: any) => updateFormData('language', e.detail.selectedOption.value as Language)}
          style={{ width: '100%' }}
        >
          {languageOptions.map(opt => (
            <Option
              key={opt.value}
              value={opt.value}
              selected={formData.language === opt.value}
            >
              {opt.label}
            </Option>
          ))}
        </Select>
      </div>
    </div>
  );

  // Step 2: Verarbeitungen auswählen
  const renderStep2 = () => (
    <div>
      <MessageStrip design="Information" hideCloseButton style={{ marginBottom: '1.5rem' }}>
        Wählen Sie die Verarbeitungen aus, die dieser Datenschutzhinweis abdecken soll. Die Inhalte werden automatisch aus den Verarbeitungsdaten generiert.
      </MessageStrip>

      {processingActivities.length === 0 ? (
        <MessageStrip design="Warning" hideCloseButton>
          Es wurden noch keine Verarbeitungstätigkeiten angelegt. Bitte erstellen Sie zuerst Verarbeitungen, bevor Sie einen Datenschutzhinweis erstellen.
        </MessageStrip>
      ) : (
        <FlexBox direction="Column" style={{ gap: '0.375rem' }}>
          {processingActivities.map(pa => (
            <div
              key={pa.id}
              onClick={() => togglePaSelection(pa.id)}
              style={{
                padding: '0.75rem 1rem',
                borderRadius: '0.5rem',
                border: selectedPaIds.includes(pa.id)
                  ? '2px solid var(--sapBrandColor)'
                  : '1px solid var(--sapField_BorderColor)',
                backgroundColor: selectedPaIds.includes(pa.id) ? 'rgba(0, 112, 242, 0.06)' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              <CheckBox
                checked={selectedPaIds.includes(pa.id)}
                onChange={() => togglePaSelection(pa.id)}
                text={pa.name}
              />
              {pa.department && (
                <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', marginLeft: '1.75rem', display: 'block' }}>
                  {pa.department}
                </Text>
              )}
            </div>
          ))}
        </FlexBox>
      )}
    </div>
  );

  // Step 3: Vorschau
  const renderStep3 = () => {
    const selectedActivities = processingActivities.filter(pa => selectedPaIds.includes(pa.id));
    const channelLabel = channelConfig[formData.channel]?.label ?? formData.channel;
    const languageLabel = languageOptions.find(l => l.value === formData.language)?.label ?? formData.language;

    return (
      <div>
        <MessageStrip design="Positive" hideCloseButton style={{ marginBottom: '1.5rem' }}>
          Der Datenschutzhinweis wird automatisch aus den Stammdaten, Verarbeitungen und Dienstleistern generiert. Sie können die einzelnen Bausteine nach dem Erstellen bearbeiten.
        </MessageStrip>

        <div style={{ marginBottom: '1.5rem' }}>
          <Title level="H5" style={{ marginBottom: '0.75rem' }}>Zusammenfassung</Title>

          <div style={{ marginBottom: '0.75rem' }}>
            <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.125rem' }}>
              Titel
            </Text>
            <Text style={{ fontWeight: '600' }}>{formData.title}</Text>
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.125rem' }}>
              Kanal
            </Text>
            <Text style={{ fontWeight: '600' }}>{channelLabel}</Text>
          </div>

          <div style={{ marginBottom: '1.25rem' }}>
            <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.125rem' }}>
              Sprache
            </Text>
            <Text style={{ fontWeight: '600' }}>{languageLabel}</Text>
          </div>

          <div>
            <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.5rem' }}>
              Ausgewählte Verarbeitungen ({selectedActivities.length})
            </Text>
            {selectedActivities.length === 0 ? (
              <Text style={{ color: 'var(--sapNeutralTextColor)' }}>Keine Verarbeitungen ausgewählt</Text>
            ) : (
              <FlexBox direction="Column" style={{ gap: '0.25rem' }}>
                {selectedActivities.map(pa => (
                  <FlexBox
                    key={pa.id}
                    alignItems="Center"
                    style={{ gap: '0.5rem' }}
                  >
                    <Icon
                      name="accept"
                      style={{ fontSize: '0.875rem', color: 'var(--sapPositiveColor)' }}
                    />
                    <div>
                      <Text style={{ fontWeight: '500' }}>{pa.name}</Text>
                      {pa.department && (
                        <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', display: 'block' }}>
                          {pa.department}
                        </Text>
                      )}
                    </div>
                  </FlexBox>
                ))}
              </FlexBox>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (initialLoading) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: return renderStep1();
      case 1: return renderStep2();
      case 2: return renderStep3();
      default: return null;
    }
  };

  const isLastStep = currentStep === lastStep;
  const isPreviewStep = !editMode && currentStep === 2;

  return (
    <div className="page-layout--narrow">
      <Button
        icon="nav-back"
        design="Transparent"
        onClick={() => navigate('/privacy-notices')}
        style={{ marginBottom: '0.75rem' }}
      >
        Zurück
      </Button>

      <Title level="H3" style={{ marginBottom: '1rem' }}>
        {editMode ? `Datenschutzhinweis bearbeiten: ${formData.title}` : 'Neuen Datenschutzhinweis erstellen'}
      </Title>

      {/* Step Indicator */}
      <div style={{ marginBottom: '1.5rem' }}>
        <ProgressIndicator
          value={Math.round(((currentStep + 1) / visibleSteps.length) * 100)}
          displayValue={`Schritt ${currentStep + 1} von ${visibleSteps.length}`}
        />
        <FlexBox justifyContent="SpaceBetween" style={{ marginTop: '0.5rem' }}>
          {visibleSteps.map((step, i) => (
            <FlexBox key={i} alignItems="Center" style={{ gap: '0.25rem' }}>
              <Icon name={step.icon} style={{
                fontSize: '0.875rem',
                color: i === currentStep ? 'var(--sapBrandColor)' :
                       i < currentStep ? 'var(--sapPositiveColor)' : 'var(--sapNeutralColor)'
              }} />
              <Text style={{
                fontSize: '0.75rem',
                fontWeight: i === currentStep ? '600' : '400',
                color: i === currentStep ? 'var(--sapBrandColor)' :
                       i < currentStep ? 'var(--sapPositiveColor)' : 'var(--sapNeutralTextColor)'
              }}>
                {step.title}
              </Text>
            </FlexBox>
          ))}
        </FlexBox>
      </div>

      {/* Error */}
      {error && (
        <MessageStrip design="Negative" hideCloseButton style={{ marginBottom: '1rem' }}>
          {error}
        </MessageStrip>
      )}

      {/* Step Content */}
      <Card style={{ marginBottom: '1rem' }}>
        <div style={{ padding: '1.5rem' }}>
          {renderStepContent()}
        </div>
      </Card>

      {/* Navigation */}
      <FlexBox justifyContent="SpaceBetween">
        <Button onClick={() => navigate('/privacy-notices')} design="Transparent">Abbrechen</Button>
        <FlexBox style={{ gap: '0.5rem' }}>
          {currentStep > 0 && (
            <Button onClick={handleBack} icon="nav-back">Zurück</Button>
          )}
          {!isLastStep ? (
            <Button onClick={handleNext} design="Emphasized" icon="feeder-arrow">
              Weiter
            </Button>
          ) : isPreviewStep ? (
            <Button onClick={handleSubmit} design="Emphasized" icon="add-document" disabled={loading}>
              {loading ? 'Wird erstellt...' : 'Erstellen'}
            </Button>
          ) : (
            <Button onClick={handleSubmit} design="Emphasized" icon="save" disabled={loading}>
              {loading ? 'Wird gespeichert...' : 'Änderungen speichern'}
            </Button>
          )}
        </FlexBox>
      </FlexBox>
    </div>
  );
}
